/* Hero banner carousel (Payload-authored slides). Moved verbatim from
   player/src/App.tsx; shared with the launcher, which passes the site origin
   for the CMS image paths and opens links externally. Styles: site.css. */
import { useEffect, useLayoutEffect, useRef, useState, type TransitionEvent } from 'react'
import { fmt } from './format'
import type { Banner, BannerApi } from './types'

// Admin-editable hero banner, now a carousel. Slides come from the Payload
// `banner` global's `slides[]` array (authored in Payload admin); one slide =
// one image + overlay copy. With a single slide it renders exactly like the old
// static hero (no controls). With several it auto-rotates with dots + arrows,
// pauses on hover/focus, supports swipe, and honours prefers-reduced-motion.
// When nothing is published we fall back to the built-in text hero so the page
// never shows an empty slot. Subhead supports a {servers} token filled with the
// live count.
type BannerData = Banner

// Relative CMS paths stay same-origin on the site; the launcher prefixes them.
const abs = (origin: string, u: string) => (origin && u.startsWith('/') ? origin + u : u)

// One slide: image + readability scrim + overlay copy. Pure render from a
// Banner; the carousel owns fetching, the frame, and the controls.
function BannerSlide({ b, serversOnline, active, origin, external }: { b: BannerData; serversOnline?: number | null; active: boolean; origin: string; external: boolean }) {
  const align = b.align === 'center' ? 'center' : 'left'
  const subhead = (b.subhead || '').replace('{servers}', fmt(serversOnline) || '')
  const scrim = align === 'center'
    ? 'radial-gradient(ellipse at center, rgba(0,0,0,.55) 0%, rgba(0,0,0,.35) 50%, rgba(0,0,0,.5) 100%)'
    : 'linear-gradient(90deg, rgba(0,0,0,.65) 0%, rgba(0,0,0,.30) 55%, rgba(0,0,0,0) 100%)'
  return (
    <div className="relative shrink-0 w-full h-full overflow-hidden" aria-hidden={!active}>
      {/* width/height are the CMS upload dimensions (2560x640 hero preset);
          CSS still sizes the element, the attributes just give the browser an
          aspect ratio before load. Inactive slides load lazily so the first
          paint only pays for the visible one. */}
      <img src={abs(origin, b.image_url)} alt={b.headline || 'dayzmetrics banner'}
           className="hero-banner-img absolute inset-0 w-full h-full"
           width={2560} height={640}
           fetchPriority={active ? 'high' : 'auto'} loading={active ? 'eager' : 'lazy'}
           decoding="async" draggable={false}
           style={{ display: 'block' }} />
      <div className="absolute inset-0" style={{ background: scrim, pointerEvents: 'none' }} aria-hidden />
      <div className="absolute inset-0 flex items-center"
           style={{ justifyContent: align === 'center' ? 'center' : 'flex-start',
                    padding: 'clamp(18px, 5vw, 72px)', textAlign: align as 'left' | 'center' }}>
        <div className="banner-inner-wrap"
             style={{ position: 'relative', zIndex: 1, maxWidth: align === 'center' ? '820px' : '58%' }}>
          <h1 className="banner-grunge"
              style={{
                fontFamily: '"Anton", "Oswald", "Geist", -apple-system, system-ui, sans-serif',
                fontWeight: 400,
                fontSize: 'clamp(34px, 5.4vw, 72px)',
                lineHeight: 0.95,
                letterSpacing: '0.005em',
                textTransform: 'uppercase',
                margin: 0,
                maxWidth: align === 'center' ? '14ch' : '12ch',
                marginLeft: align === 'center' ? 'auto' : 0,
                marginRight: align === 'center' ? 'auto' : 0,
              }}>
            {b.headline || 'Find a DayZ server worth playing'}
          </h1>
          {subhead ? (
            <p className="mt-5 leading-snug"
               style={{ color: 'rgba(255,255,255,.88)', fontSize: 'clamp(14px, 1.15vw, 18px)',
                        maxWidth: '46ch', marginLeft: align === 'center' ? 'auto' : 0,
                        marginRight: align === 'center' ? 'auto' : 0,
                        textShadow: '0 1px 12px rgba(0,0,0,.5)' }}>
              {subhead}
            </p>
          ) : null}
          {b.link_url && b.link_label ? (
            <div className="mt-5">
              <a href={abs(origin, b.link_url)}
                 className="mono text-xs uppercase tracking-wider inline-flex items-center gap-2 rounded-full px-4 py-2.5"
                 style={{ background: 'var(--brand)', color: 'var(--accent-foreground)', textDecoration: 'none' }}
                 target={external || b.link_url.startsWith('http') ? '_blank' : undefined}
                 rel={external || b.link_url.startsWith('http') ? 'noopener noreferrer' : undefined}>
                {b.link_label}
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const BANNER_CHEV_L = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
const BANNER_CHEV_R = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>

export function HeroCarousel({ api, serversOnline, origin = '', external = false }: {
  api: BannerApi; serversOnline?: number | null; origin?: string; external?: boolean
}) {
  const [slides, setSlides] = useState<BannerData[] | null>(null)
  const [shown, setShown] = useState(0)       // logical (0-based) index, drives the counter + bar key
  const [paused, setPaused] = useState(false)
  const reduce = useRef(false)
  const trackRef = useRef<HTMLDivElement>(null)
  const posRef = useRef(1)                     // index into the cloned track (real slide k = pos k+1)
  const animating = useRef(false)

  useEffect(() => {
    reduce.current = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    api.banners().then(setSlides).catch(() => setSlides([]))
  }, [api])

  const n = slides?.length ?? 0

  // Drive the track transform imperatively (NOT via the style prop) so setShown
  // re-renders never reset the scroll position. The reflow makes a transition-
  // then-transform in the same tick actually animate instead of jumping.
  const setX = (pos: number, animate: boolean) => {
    const el = trackRef.current
    if (!el) return
    if (animate && !reduce.current) {
      el.style.transition = 'transform .55s cubic-bezier(.4,0,.2,1)'
      void el.offsetWidth
      el.style.transform = `translateX(-${pos * 100}%)`
    } else {
      el.style.transition = 'none'
      el.style.transform = `translateX(-${pos * 100}%)`
      void el.offsetWidth
    }
  }

  // Snap clone -> matching real slide and release the lock. Idempotent.
  const settle = () => {
    animating.current = false
    if (posRef.current === n + 1) { posRef.current = 1; setX(1, false) }
    else if (posRef.current === 0) { posRef.current = n; setX(n, false) }
  }

  // Park on the first real slide before paint (clone-of-last sits at index 0).
  useLayoutEffect(() => {
    if (n > 1) { posRef.current = 1; setX(1, false) }
  }, [n]) // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite advance: posRef keeps moving in the requested direction; landing on
  // a clone snaps (transition-less) back to the matching real slide once the
  // slide finishes, so "next" always slides right and wraps without going back.
  const go = (dir: number) => {
    if (n < 2 || animating.current) return
    posRef.current += dir
    setShown(((posRef.current - 1) % n + n) % n)  // new key -> the progress bar restarts
    if (reduce.current) {
      setX(posRef.current, false)
      if (posRef.current === n + 1) { posRef.current = 1; setX(1, false) }
      else if (posRef.current === 0) { posRef.current = n; setX(n, false) }
    } else {
      animating.current = true
      setX(posRef.current, true)
      window.setTimeout(settle, 650)  // safety net if transitionend is missed
    }
  }
  const onEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.target !== trackRef.current || e.propertyName !== 'transform') return
    settle()
  }

  // Loading: hold the frame so the page never jumps when the image swaps in.
  if (slides === null) {
    return (
      <div className="hero-banner-outer">
        <div className="hero-banner-frame relative w-full rounded-2xl overflow-hidden"
             style={{ background: 'var(--background-secondary)' }} />
      </div>
    )
  }
  // Nothing published: the built-in text hero.
  if (n === 0) {
    return (
      <div style={{ marginTop: 80, marginBottom: 80 }}>
        <h1 className="text-2xl sm:text-[28px] font-semibold tracking-tight">Find a DayZ server worth playing.</h1>
        <p className="text-[13px] text-[var(--muted)] mt-3 max-w-2xl leading-relaxed">
          Real-time player counts across {fmt(serversOnline)} servers, fakes removed.
          Compare maps, mods, ping and busiest hours, then jump in.
        </p>
      </div>
    )
  }

  const multi = n > 1
  // Cloned track: [clone(last), ...real, clone(first)] so the loop is seamless.
  const rendered = multi ? [slides[n - 1], ...slides, slides[0]] : slides
  const pad = (k: number) => String(k + 1).padStart(2, '0')

  return (
    <div className="hero-banner-outer"
         onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
         onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}>
      <div className="hero-banner-frame relative w-full rounded-2xl overflow-hidden"
           style={{ background: 'var(--background-secondary)' }}
           role="group" aria-roledescription="carousel" aria-label="Homepage banners">
        {/* transform driven imperatively (setX) — keep it OUT of the style prop */}
        <div ref={trackRef} className="flex h-full w-full" onTransitionEnd={onEnd}
             style={{ willChange: 'transform' }}>
          {rendered.map((b, idx) => <BannerSlide key={idx} b={b} serversOnline={serversOnline} active={idx === 1} origin={origin} external={external} />)}
        </div>

        {multi && (
          <div className="absolute z-[3] flex items-center" style={{ right: 16, bottom: 14, gap: 12, color: '#fff' }}>
            <button type="button" aria-label="Previous banner" className="banner-mini" onClick={() => go(-1)}>{BANNER_CHEV_L}</button>
            <span className="mono flex items-center" style={{ fontSize: 13, gap: 10 }} aria-hidden>
              <b style={{ color: 'var(--brand)', fontWeight: 500 }}>{pad(shown)}</b>
              {/* progress: a single GPU-composited scaleX animation (smooth, no
                  per-frame JS). key={shown} restarts it each slide; finishing it
                  fires onAnimationEnd which advances. Hidden under reduced motion
                  (no auto-advance for those users). */}
              <span className="hero-bar-track">
                {!reduce.current && (
                  <span key={shown} className="hero-bar-fill"
                        style={{ animationDuration: '6s', animationPlayState: paused ? 'paused' : 'running' }}
                        onAnimationEnd={() => go(1)} />
                )}
              </span>
              <span style={{ color: 'rgba(255,255,255,.7)' }}>{String(n).padStart(2, '0')}</span>
            </span>
            <button type="button" aria-label="Next banner" className="banner-mini" onClick={() => go(1)}>{BANNER_CHEV_R}</button>
          </div>
        )}
      </div>
    </div>
  )
}
